import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiExternalLink,
  FiPrinter,
  FiShare2,
  FiX,
} from 'react-icons/fi';
import {
  fetchVaultAttachmentBlob,
  getVaultAttachmentSignedUrl,
} from './vault_file_api';

const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif']);

function extOf(finfo) {
  return String(finfo?.extn || finfo?.file_name?.split('.').pop() || '')
    .toLowerCase()
    .replace(/^\./, '');
}

function kindOf(finfo) {
  const ext = extOf(finfo);
  if (IMAGE_EXTS.has(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  return 'other';
}

function displayName(finfo) {
  return finfo?.file_name || finfo?.name || 'Attachment';
}

function mimeForKind(kind, ext) {
  if (kind === 'pdf') return 'application/pdf';
  if (kind === 'image') {
    const mimeExt = ext === 'jpg' ? 'jpeg' : ext;
    return `image/${mimeExt || 'jpeg'}`;
  }
  return '';
}

/**
 * Full-screen modal to preview vault attachments (image / PDF),
 * with prev/next when a note has multiple files.
 */
const VaultAttachmentViewer = ({ files, initialIndex = 0, onClose }) => {
  const list = useMemo(
    () => (Array.isArray(files) ? files.filter(Boolean) : []),
    [files]
  );
  const [index, setIndex] = useState(() =>
    Math.max(0, Math.min(initialIndex, Math.max(0, list.length - 1)))
  );
  const [url, setUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const iframeRef = useRef(null);

  const current = list[index] || null;
  const kind = current ? kindOf(current) : 'other';
  const canPrev = index > 0;
  const canNext = index < list.length - 1;
  const multi = list.length > 1;
  const actionsDisabled = !current || current.pending || loading || !url;

  useEffect(() => {
    setIndex(Math.max(0, Math.min(initialIndex, Math.max(0, list.length - 1))));
  }, [initialIndex, list.length]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    if (!current || current.pending) {
      setUrl(null);
      setBlob(null);
      setLoading(false);
      setError(current?.pending ? 'Upload still in progress.' : 'No file selected.');
      return undefined;
    }

    setLoading(true);
    setError('');
    setActionError('');
    setUrl(null);
    setBlob(null);

    (async () => {
      try {
        // Same-origin API stream — does not require R2 bucket CORS.
        let fileBlob = await fetchVaultAttachmentBlob(current);
        const fileKind = kindOf(current);
        const ext = extOf(current);
        const wanted = mimeForKind(fileKind, ext);
        if (wanted && fileBlob.type !== wanted) {
          fileBlob = new Blob([fileBlob], { type: wanted });
        }
        objectUrl = URL.createObjectURL(fileBlob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setBlob(fileBlob);
        setUrl(objectUrl);
        setLoading(false);
      } catch (err) {
        console.error('Attachment preview failed:', err);
        if (!cancelled) {
          setError(err?.message || 'Could not load attachment.');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [current]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      } else if (e.key === 'ArrowLeft' && canPrev) {
        e.preventDefault();
        setIndex((i) => i - 1);
      } else if (e.key === 'ArrowRight' && canNext) {
        e.preventDefault();
        setIndex((i) => i + 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, canPrev, canNext]);

  const openExternally = async () => {
    try {
      const signed = await getVaultAttachmentSignedUrl(current);
      const opened = window.open(signed, '_blank');
      if (!opened) throw new Error('Popup blocked');
    } catch (err) {
      console.error(err);
      setActionError('Unable to open in a new tab. Check popup settings.');
    }
  };

  const downloadCurrent = () => {
    if (!url || !current) return;
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = displayName(current);
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error(err);
      setActionError('Download failed.');
    }
  };

  const printCurrent = () => {
    if (!url) return;
    try {
      if (kind === 'pdf' && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.focus();
        iframeRef.current.contentWindow.print();
        return;
      }
      const w = window.open(url, '_blank');
      if (!w) throw new Error('Popup blocked');
      const trigger = () => {
        try {
          w.focus();
          w.print();
        } catch (err) {
          console.error(err);
        }
      };
      // Images / PDFs may need a tick before print is ready.
      w.addEventListener?.('load', trigger);
      setTimeout(trigger, 400);
    } catch (err) {
      console.error(err);
      setActionError('Unable to print. Try opening in a new tab.');
    }
  };

  const shareCurrent = async () => {
    if (!current) return;
    setActionError('');
    try {
      const name = displayName(current);
      const file =
        blob && typeof File !== 'undefined'
          ? new File([blob], name, { type: blob.type || 'application/octet-stream' })
          : null;

      if (file && navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: name });
        return;
      }

      if (navigator.share) {
        const signed = await getVaultAttachmentSignedUrl(current);
        await navigator.share({ title: name, url: signed });
        return;
      }

      // Fallback: copy signed URL when Web Share is unavailable.
      const signed = await getVaultAttachmentSignedUrl(current);
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(signed);
        setActionError('Link copied to clipboard.');
        return;
      }
      downloadCurrent();
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error(err);
      setActionError('Share failed.');
    }
  };

  if (!list.length) return null;

  return (
    <div
      className="vault-viewer-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="vault-viewer"
        role="dialog"
        aria-modal="true"
        aria-label={displayName(current)}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="vault-viewer-header">
          <button
            type="button"
            className="vault-viewer-btn"
            onClick={onClose}
            title="Close"
          >
            <FiX />
            <span>Close</span>
          </button>
          <div className="vault-viewer-title" title={displayName(current)}>
            {displayName(current)}
          </div>
          <div className="vault-viewer-actions">
            <button
              type="button"
              className="vault-viewer-btn vault-viewer-btn--icon"
              onClick={printCurrent}
              title="Print"
              disabled={actionsDisabled || (kind !== 'pdf' && kind !== 'image')}
            >
              <FiPrinter />
            </button>
            <button
              type="button"
              className="vault-viewer-btn vault-viewer-btn--icon"
              onClick={shareCurrent}
              title="Share"
              disabled={actionsDisabled}
            >
              <FiShare2 />
            </button>
            <button
              type="button"
              className="vault-viewer-btn vault-viewer-btn--icon"
              onClick={downloadCurrent}
              title="Download"
              disabled={actionsDisabled}
            >
              <FiDownload />
            </button>
            <button
              type="button"
              className="vault-viewer-btn vault-viewer-btn--icon"
              onClick={openExternally}
              title="Open in new tab"
              disabled={!current || current.pending}
            >
              <FiExternalLink />
            </button>
          </div>
        </header>

        {actionError ? (
          <div className="vault-viewer-banner" role="status">
            {actionError}
          </div>
        ) : null}

        <div className={`vault-viewer-body${multi ? '' : ' vault-viewer-body--single'}`}>
          {multi ? (
            <button
              type="button"
              className="vault-viewer-nav vault-viewer-nav--prev"
              disabled={!canPrev}
              onClick={() => setIndex((i) => i - 1)}
              aria-label="Previous attachment"
            >
              <FiChevronLeft />
            </button>
          ) : null}

          <div className="vault-viewer-stage">
            {loading ? (
              <div className="vault-viewer-status">
                <div className="vault-viewer-spinner" />
                <span>Loading…</span>
              </div>
            ) : error ? (
              <div className="vault-viewer-status">
                <p>{error}</p>
                <button type="button" className="vault-compose-submit" onClick={openExternally}>
                  Open in new tab
                </button>
              </div>
            ) : kind === 'image' && url ? (
              <img className="vault-viewer-image" src={url} alt={displayName(current)} />
            ) : kind === 'pdf' && url ? (
              <iframe
                ref={iframeRef}
                className="vault-viewer-pdf"
                title={displayName(current)}
                src={url}
              />
            ) : (
              <div className="vault-viewer-status">
                <p>Preview isn’t available for this file type.</p>
                <div className="vault-viewer-status-actions">
                  <button type="button" className="vault-compose-submit" onClick={downloadCurrent}>
                    Download
                  </button>
                  <button type="button" className="vault-compose-cancel" onClick={openExternally}>
                    Open in new tab
                  </button>
                </div>
              </div>
            )}
          </div>

          {multi ? (
            <button
              type="button"
              className="vault-viewer-nav vault-viewer-nav--next"
              disabled={!canNext}
              onClick={() => setIndex((i) => i + 1)}
              aria-label="Next attachment"
            >
              <FiChevronRight />
            </button>
          ) : null}
        </div>

        {multi ? (
          <footer className="vault-viewer-footer">
            {index + 1} of {list.length}
          </footer>
        ) : null}
      </div>
    </div>
  );
};

export default VaultAttachmentViewer;
export { IMAGE_EXTS, extOf, kindOf };
